import { getEcho } from '@/services/echo';
import type { Ref } from 'vue';
import { onUnmounted, watch } from 'vue';

export type AsyncOperationFinishedPayload = {
  operation_id: string;
  type: string;
  status: string;
  error_code?: string | null;
  meta?: Record<string, unknown>;
};

type Options = {
  onFinished?: (payload: AsyncOperationFinishedPayload) => void;
};

export function useAsyncOperationSubscription(operationId: Ref<string | null>, options: Options): void {
  let subscribedOperationId: string | null = null;

  function cleanup(): void {
    if (subscribedOperationId === null) {
      return;
    }
    const echo = getEcho();
    const opId = subscribedOperationId;
    subscribedOperationId = null;
    if (echo !== null) {
      echo.leave(`private-async-operations_${opId}`);
    }
  }

  watch(
    operationId,
    (id) => {
      cleanup();
      if (id === null || id === '') {
        return;
      }
      const echo = getEcho();
      if (echo === null) {
        return;
      }
      subscribedOperationId = id;
      echo
        .private(`async-operations_${id}`)
        .listen('.AsyncOperationFinished', (payload: unknown) => {
          options.onFinished?.(payload as AsyncOperationFinishedPayload);
          cleanup();
        });
    },
    // `immediate` matters. Most callers start this ref at null and assign the id
    // later, so the assignment is a change and the watcher fires. But a caller
    // that receives an id it already has — TrackListExportWatcher takes one as a
    // prop — never changes it, so without `immediate` the callback never runs and
    // no Echo subscription is ever opened. The null guard above makes the
    // immediate run a no-op for the callers that do start at null.
    { flush: 'post', immediate: true },
  );

  onUnmounted(() => {
    cleanup();
  });
}
